import _ from 'lodash'
import moment from 'moment'
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Pretty from 'prettysize'
import Dropzone from 'react-dropzone'
import pluralize from 'pluralize'
import classnames from 'classnames/bind'
import Accumulator from '../../layouts/accumulator'
import Button from '../../elementals/button'
import Icon from '../../elementals/icon'
import Ribbon from '../../elementals/ribbon'
import {
  apiStringToLocalMoment,
  removeFileExtension,
  dateToApiString,
} from '../../../utils/formatters'
import { MAX_FILE_SIZE as DEFAULT_MAX_FILE_SIZE } from '../../../constants/statics'
import { retrieveImageFromClipboard, getImageURLFromBlob } from '../imageBlob'
import isMobileBrowser from '../../../utils/isMobileBrowser'
import PhotoCard from '../photoCard'
import PhotoDeleteModal from './photoDeleteModal'
import PhotoCarouselModal from './photoCarouselModal'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const DEFAULT_MAX_PHOTOS_ALLOWED = 50
const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/png']

/**
 * @param {String} image file name object
 * @returns {String} image file name without the extension
 */
const removeImageFileExtension = imageFileName =>
  removeFileExtension(imageFileName, ['jpeg', 'png', 'jpg'])

/**
 * Emulation of what a ribbon calculation might look like when we implement this for realsies.
 *
 * @param {Object} option
 * @param {Number} index
 */
const getRibbon = (option, index) => {
  const now = moment()
  let text = ''
  let color = ''

  if (index === 0) {
    text = 'Cover Photo'
    color = 'aquaBlue'
  } else if (
    moment
      .duration(now.diff(apiStringToLocalMoment(option.updatedOn)))
      .hours() < 1
  ) {
    text = 'Newly Added'
    color = 'adcLightGreen'
  } else if (
    moment
      .duration(now.diff(apiStringToLocalMoment(option.updatedOn)))
      .years() > 0
  ) {
    text = 'Stale Photo?'
    color = 'caution'
  }
  return !text ? undefined : (
    <Ribbon size='tiny' color={color}>
      {text}
    </Ribbon>
  )
}

const photoRenderer = ({
  isSelected,
  onAddOrRemove,
  option,
  index,
  onDoubleClick,
  onTagChange,
  propertyId,
  showPhotoOnly,
  allowEditing,
  allowTagging,
}) => (
  <PhotoCard
    key={option.value}
    id={option.value}
    tag={option.tag}
    onDelete={onAddOrRemove}
    onUndelete={onAddOrRemove}
    onTagChange={onTagChange}
    isSelected={isSelected}
    disableTag={!isSelected}
    title={isSelected ? (index + 1).toString() : ''}
    createdOn={option.createdOn}
    updatedOn={option.updatedOn}
    onDoubleClick={onDoubleClick}
    ribbon={getRibbon(option, index)}
    photoUrl={option.photoUrl}
    photoId={option.id}
    propertyId={propertyId}
    allowEditing={allowEditing}
    allowTagging={allowTagging}
    showPhotoOnly={showPhotoOnly}
  />
)

const PhotoManagement = ({
  photos = [],
  property = {},
  updatePhotos,
  settings = {},
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxPhotosAllowed = DEFAULT_MAX_PHOTOS_ALLOWED,
}) => {
  const [photoToDelete, setDeletePhoto] = useState({})
  const [trigger, setTrigger] = useState(0)
  const [carouselPhotoId, setCarouselPhotoId] = useState('')
  const [isCarouselOpen, toggleCarousel] = useState(false)
  const [isDeleteModalOpen, toggleDeleteModal] = useState(false)
  const [autoScrollDown, setAutoScrollDown] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    window.addEventListener('paste', pasteHandler, false)
    return () => {
      window.removeEventListener('paste', pasteHandler, false)
    }
  }, [photos]) // eslint-disable-line react-hooks/exhaustive-deps

  const pasteHandler = e => {
    const imageBlob = retrieveImageFromClipboard(e)
    if (imageBlob) {
      const url = getImageURLFromBlob(imageBlob)
      onPhotoChange([
        {
          name: `paste_img_${new Date().getTime().toString()}`, // add timestamp to avoid dupes (per listing)
          preview: url,
          imageBlob,
        },
      ])
    }
  }

  const onTagChange = value => label => {
    const photo = photos?.find(p => p.value === value)
    if (!photo) {
      return
    }
    photo.tag = label
  }

  /* istanbul ignore next */
  const validateFiles = (files = [], photos) => {
    if (files.length + photos.length > maxPhotosAllowed) {
      const remainingPhotos = maxPhotosAllowed - photos.length
      if (remainingPhotos <= 0) {
        setError(
          `The maximum number of uploaded images is ${maxPhotosAllowed}.`
        )
      } else {
        setError(
          `The maximum number of uploaded images is ${maxPhotosAllowed} (You can upload ${
            maxPhotosAllowed - photos.length
          } more ${pluralize('image', maxPhotosAllowed - photos.length)}).`
        )
      }
      return false
    }

    // This is for one of those rare cases if someone tries to upload
    // files with the same name but different extensions like pic_1.png and pic_1.jpg
    const fileNames = new Set(
      files.map(file => removeImageFileExtension(file.name))
    )

    if (fileNames.size < files.length) {
      setError('No duplicate file names allowed')
      return false
    }

    const uploadedPhotoNames = new Set(photos.map(photo => photo.value))

    const duplicateFileNames = [...fileNames].filter(fileName =>
      uploadedPhotoNames.has(fileName)
    )
    if (!_.isEmpty(duplicateFileNames)) {
      setError(
        `You already have ${pluralize(
          'file',
          duplicateFileNames.length
        )}: ${duplicateFileNames.join(', ')}`
      )
      return false
    }

    error && setError(null)
    return true
  }

  const getSelectedPhotos = photos =>
    photos?.filter(photo => photo.isSelected)?.map(photo => photo.value)

  const onSelectedPhotoUpdate = selectedPhotoVals => {
    const currentSelected = new Set(selectedPhotoVals)
    photos.forEach(
      photo => (photo.isSelected = currentSelected.has(photo.value))
    )
    // reorder the photos based on the val arrs from the accumulator
    const selectedPhotos = selectedPhotoVals.map(val =>
      _.find(photos, { value: val })
    )
    const unselectedPhotos = _.differenceBy(photos, selectedPhotos, 'value')
    updatePhotos(selectedPhotos.concat(unselectedPhotos))
  }

  /* istanbul ignore next */
  const onPhotoChange = (files = []) => {
    if (!validateFiles(files, photos)) return
    setAutoScrollDown(true) // we only want auto-scroll to bottom when we add an image
    const photosToUpdate = files.map(file => {
      return {
        value: removeImageFileExtension(file.name),
        photoUrl: file.preview,
        tag: '',
        createdOn: dateToApiString(), // now
        updatedOn: dateToApiString(), // now
        isSelected: true,
        isNewUploaded: true,
        openDeleteModal,
        file: file.imageBlob || file, // imageBlob passed as sub-property in the case of paste from clipboard
      }
    })
    updatePhotos(photos.concat(photosToUpdate))
    setAutoScrollDown(false)
  }

  /* istanbul ignore next */
  const onPhotoChangeRejected = rejectedFiles => {
    const notAcceptedFileTypeFileNames = rejectedFiles.reduce((acc, file) => {
      if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
        acc.push(file.name)
      }
      return acc
    }, [])

    if (!_.isEmpty(notAcceptedFileTypeFileNames)) {
      setError(
        `${pluralize(
          'File',
          notAcceptedFileTypeFileNames.length
        )}: ${notAcceptedFileTypeFileNames.join(
          ', '
        )} are not of accepted file type of ${ACCEPTED_FILE_TYPES.join(', ')}`
      )
      return
    }

    const oversizedFileNames = rejectedFiles.reduce((acc, file) => {
      if (file.size > maxFileSize) {
        acc.push(`${removeImageFileExtension(file.name)}(${Pretty(file.size)})`)
      }
      return acc
    }, [])

    if (!_.isEmpty(oversizedFileNames)) {
      setError(
        `${pluralize(
          'File',
          oversizedFileNames.length
        )}: ${oversizedFileNames.join(
          ', '
        )} exceed the max file size limit of ${Pretty(maxFileSize)}`
      )
    }
  }

  /**
   * This is to control when user attempts to move a newly uploaded photo over to unused column.
   * In this case we do the following:
   *   1) on the unselected list, we setDeletePhoto so we know a deletion is taking place
   *   2) on the select list, we check if there's a photoToDelete and if so prompt user (as if they clicked trash can)
   *
   * In either case, we don't want to allowUpdate to either list:
   *   the unselect list should never change in this case and
   *   the selected list will only get updated if the user confirms delete.
   */
  const allowUpdate = (options, isSelectedList) => {
    if (!isSelectedList) {
      const newPhotoBeingDeleted = options.find(p => p.isNewUploaded)
      if (newPhotoBeingDeleted) {
        setDeletePhoto(newPhotoBeingDeleted)
        return false
      }
    } else {
      // selected list
      if (!_.isEmpty(photoToDelete)) {
        openDeleteModal(photoToDelete)
        return false
      } else if (settings.hideUnusedPhotos) {
        setTimeout(() => {
          /**
           * We normally just let the photo move to the unused list if it's not new, however, in the case of
           * hideUnusedPhotos, since user can't undo a delete,  we want to prompt the user to confirm deletion
           * the preexisting photo before moving the new photo to the unused list.
           */
          const preexistingPhotoToDelete = _.find(options, [
            'isSelected',
            false,
          ])
          if (!_.isEmpty(preexistingPhotoToDelete)) {
            setDeletePhoto(preexistingPhotoToDelete)
            openDeleteModal(preexistingPhotoToDelete)
            return false
          }
        }, 10)
        return true
      }
    }
    return true
  }

  const selectedPhotoRenderer = (onRemove, option, index) =>
    photoRenderer({
      isSelected: true,
      onAddOrRemove: onRemove,
      option,
      index,
      onDoubleClick,
      onTagChange,
      propertyId: property?.property_id,
      allowEditing: settings.allowEditing,
      allowTagging: settings.allowTagging,
      showPhotoOnly: settings.showPhotoOnly,
    })

  const unselectedPhotoRenderer = (onAdd, option) =>
    photoRenderer({
      isSelected: false,
      onAddOrRemove: onAdd,
      option,
      onDoubleClick,
      onTagChange,
      allowEditing: settings.allowEditing,
      allowTagging: settings.allowTagging,
      showPhotoOnly: settings.showPhotoOnly,
    })

  const openDeleteModal = deletePhoto => {
    toggleDeleteModal(true)
    setDeletePhoto(deletePhoto)
  }

  const closeDeleteModal = () => {
    toggleDeleteModal(false)
    setDeletePhoto({})
  }

  const onDeleteCancel = () => {
    if (
      settings.hideUnusedPhotos &&
      photoToDelete &&
      !photoToDelete.isSelected
    ) {
      photoToDelete.isSelected = true
      setTrigger(trigger + 1) // trigger to update unselected list behind the scenes
    }
    closeDeleteModal()
  }

  const onDeleteConfirm = () => {
    if (settings.hideUnusedPhotos && !photoToDelete.isNewUploaded) {
      // do nothing - in this case we're just moving it to unselected (pre-existing photo)
      closeDeleteModal()
      return
    }

    const deleteIndex = _.findIndex(
      photos,
      p => p.value === photoToDelete.value
    )
    if (deleteIndex !== -1) {
      photos.splice(deleteIndex, 1)
    }
    updatePhotos(photos)
    closeDeleteModal()
  }

  const onDoubleClick = id => {
    toggleCarousel(true)
    setCarouselPhotoId(id)
  }

  const onCarouselClose = () => {
    toggleCarousel(false)
    setCarouselPhotoId('')
  }

  return (
    <div
      className={cx('photo-management-container')}
      data-elm-id='photo_management'
    >
      <Accumulator
        showAsGrid
        height={400}
        allOptions={photos}
        initialSelectedValues={getSelectedPhotos(photos)}
        onUpdate={onSelectedPhotoUpdate}
        selectedListItemRenderer={selectedPhotoRenderer}
        unselectedListItemRenderer={unselectedPhotoRenderer}
        selectedListHeader={
          settings.hideUnusedPhotos ? 'Photos' : 'Photos to Show'
        }
        unselectedListHeader='Unused Photos'
        unselectedSortKey={{ key: 'updatedOn', direction: 'desc' }}
        autoScrollDown={autoScrollDown}
        allowUpdate={allowUpdate}
        triggerUpdate={trigger}
        hideRightPanel={settings.hideUnusedPhotos}
      />
      <div className={cx('photo-management-footer')}>
        <div className={cx('panel', 'left-side')}>
          <div className={cx('item')} data-elm-id='drop_files_zone'>
            <Icon
              className={cx('chev-icon')}
              iconStyle='solid'
              name='chevron-double-up'
              color='grayLight'
              size='large'
            />
            <Dropzone
              className={cx('dropzone')}
              acceptClassName={cx('accept-dropzone')}
              activeClassName={cx('active-dropzone')}
              rejectClassName={cx('reject-dropzone')}
              onDrop={onPhotoChange}
              onDropRejected={onPhotoChangeRejected}
              accept={ACCEPTED_FILE_TYPES}
              multiple
              maxSize={maxFileSize}
            >
              <Button
                size='large'
                style={{ borderStyle: 'dashed' }}
                className={error ? cx('dropzone-error') : ''}
              >
                <Icon name='cloud-upload-alt' iconStyle='solid' />
                {isMobileBrowser() ? ' Tap to Add Photos' : ' Drop Files Here'}
              </Button>
            </Dropzone>
            {error && (
              <div
                className={cx('dropzone-error-message')}
                data-elm-id='dropzone_error_message'
              >
                {error}
              </div>
            )}
          </div>
        </div>
        {!settings.hideUnusedPhotos && (
          <div className={cx('panel', 'right-side')} />
        )}
        <PhotoDeleteModal
          isOpen={isDeleteModalOpen}
          photoToDelete={photoToDelete}
          onDeleteConfirm={onDeleteConfirm}
          onDeleteCancel={onDeleteCancel}
          subtitle={settings.hideUnusedPhotos ? '' : null}
        />
        <PhotoCarouselModal
          isOpen={isCarouselOpen}
          onClose={onCarouselClose}
          photos={photos}
          address={_.get(property, 'summary.street_address')}
          photoId={carouselPhotoId}
        />
      </div>
    </div>
  )
}

PhotoManagement.propTypes = {
  photos: PropTypes.array,
  property: PropTypes.object,
  updatePhotos: PropTypes.func.isRequired,
  settings: PropTypes.shape({
    showPhotoOnly: PropTypes.bool,
    allowEditing: PropTypes.bool,
    allowTagging: PropTypes.bool,
    hideUnusedPhotos: PropTypes.bool,
  }),
  maxFileSize: PropTypes.number,
  maxPhotosAllowed: PropTypes.number,
}

export { PhotoCarouselModal, getRibbon }
export default PhotoManagement
